/**
 * Reading a process job's `inputJson` back as named parameters.
 *
 * A job row records exactly what it was started with — the projection, every
 * checkbox, every advanced knob — but only as an opaque JSON blob. Support
 * questions ("what SRID did that upload run with?", "was 'reset
 * classification' ticked?") are answerable from it, so the data centre shows
 * it to internal staff (see `isInternalUser`). This module turns the blob into
 * a labelled list.
 *
 * Labels are borrowed, not invented. Everything under `inputJson.params` came
 * from a run dialog whose form fields are already translated in all five
 * locales under a per-feature namespace (`groundClassification.runElm`,
 * `deviationAnalysis.volumeResolution`, …), and the param names match those
 * key names almost 1:1 — `JOB_TYPE_PARAM_NS` is what connects the two. Keys
 * outside `params` belong to the upload dialog, whose labels are translated
 * under `batch.*` but whose key names do not match the payload's, so
 * `SHARED_PARAM_I18N` aliases those by hand. Anything left over is humanized
 * from the key itself, which stays readable even for a parameter added long
 * after this file was last touched.
 */
import type { ProcessJobType } from '@/graphql/graphql';

/** One leaf of `inputJson`. */
export interface JobParamEntry {
  /** Dotted path from the root of `inputJson`, e.g. `params.smrfCell`. */
  path: string;
  /** The leaf's own key, e.g. `smrfCell`. */
  key: string;
  /** The enclosing block's key, or `null` at the root. */
  parentKey: string | null;
  /** True for a leaf directly under `inputJson.params` — the run dialog's fields. */
  inParams: boolean;
  value: unknown;
}

export interface JobParamGroups {
  /** What a human started the job with. */
  params: JobParamEntry[];
  /** S3 keys, ids, folders — needed to debug, useless to read. Collapsed in the UI. */
  internals: JobParamEntry[];
}

/**
 * Job type → the `project` i18n namespace whose keys are that job's dialog
 * fields. Only run-from-a-dialog jobs have one; upload jobs (LAS, E57, …) fall
 * through to the shared dictionary, which is why this is `Partial`.
 */
export const JOB_TYPE_PARAM_NS: Partial<Record<ProcessJobType, string>> = {
  DEVIATION: 'deviationAnalysis',
  GROUND_CLASSIFY: 'groundClassification',
  SURFACE_DIFFERENCE: 'surfaceDifference',
  CLEARANCE: 'clearance',
  RAINFALL_ANALYSIS: 'rainfallAnalysis',
  VECTOR_EXPORT: 'vectorExport',
  LANDXML_EXPORT: 'landxmlExport',
  CLIPBOX_EXPORT: 'clipboxExport',
  MESH: 'meshExport',
  PDF_REPORT: 'report',
  LAYER_STACK: 'layerStack',
};

/**
 * Payload keys whose label already exists — as an upload-dialog field, under a
 * different name. Everything else outside `params` humanizes acceptably
 * (`direction` → "Direction"), so this list stays short on purpose: an alias
 * is only worth adding when the humanized key would actually mislead.
 */
export const SHARED_PARAM_I18N: Record<string, string> = {
  srid: 'batch.srid',
  pointCloudAttributes: 'batch.pointCloudAttributes',
  generateGeotiff: 'batch.generateGeotiff',
  generateRcs: 'batch.generateRcs',
  generate2dMap: 'batch.generate2dMap',
  generateDecimatedPointCloud: 'batch.generateDecimated',
  compressImages: 'batch.compressImages',
  blurFaces: 'batch.blurFaces',
  blurLicensePlates: 'batch.blurLicensePlates',
  rasterMode: 'batch.rasterMode',
};

/** Keys that are plumbing whatever their value looks like. */
const INTERNAL_KEYS = new Set([
  'outputFolder',
  'inputFile',
  'inputFiles',
  'outputFile',
  'outputKey',
  'imageFiles',
  'sampleImageFile',
  'contentLength',
  'sourceAttachmentId',
  'logStream',
  // MAP_2D routing/provenance flags (map2d.ts), not user parameters.
  'route',
  'origin',
]);

/** `sourceLazS3Key`, `stationVolumesS3Key`, `layerStackS3Key`, … */
const S3_KEY_RE = /s3key$/i;
/** `sourceLayerId`, `batchId`, `featureIds` — but never `srid`, which is lower-case. */
const ID_KEY_RE = /Ids?$/;
/** A one-line value stops being readable long before this. */
const MAX_INLINE_STRING = 300;
/** `srid`, `tifSrid`, `projectSrid`. */
const SRID_KEY_RE = /srid$/i;

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const isInternalEntry = (key: string, value: unknown): boolean => {
  if (INTERNAL_KEYS.has(key) || S3_KEY_RE.test(key) || ID_KEY_RE.test(key)) return true;
  if (Array.isArray(value)) return value.some((v) => typeof v === 'object' && v !== null);
  if (typeof value === 'string' && value.length > MAX_INLINE_STRING) return true;
  return false;
};

/** How deep we expand nested option blocks before showing them as raw JSON. */
const MAX_DEPTH = 2;

/**
 * Flatten `inputJson` into two ordered lists, preserving the order the job's
 * payload was built in — which reads far better than alphabetical, because the
 * worker's own code groups related options together.
 */
export const flattenJobParams = (inputJson: unknown): JobParamGroups => {
  const groups: JobParamGroups = { params: [], internals: [] };
  if (!isPlainObject(inputJson)) return groups;

  const walk = (node: Record<string, unknown>, prefix: string, parentKey: string | null, depth: number) => {
    for (const [key, value] of Object.entries(node)) {
      if (value === undefined) continue;
      const path = prefix ? `${prefix}.${key}` : key;
      if (isPlainObject(value) && depth < MAX_DEPTH && !isInternalEntry(key, value)) {
        walk(value, path, key, depth + 1);
        continue;
      }
      const entry: JobParamEntry = { path, key, parentKey, inParams: prefix === 'params', value };
      groups[isInternalEntry(key, value) ? 'internals' : 'params'].push(entry);
    }
  };

  walk(inputJson, '', null, 0);
  return groups;
};

/** `volumeResolution` becomes `Volume resolution`. Last-resort label. */
export const humanizeParamKey = (key: string): string => {
  const spaced = key.replace(/([a-z0-9])([A-Z])/g, '$1 $2').toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

/**
 * Minimal shape of i18next's `t` — keeps this module testable without i18n.
 *
 * Deliberately looser than the app's `TFunction`, whose key parameter is a
 * union of every literal key in the bundle: these keys are assembled at
 * runtime from a job type and a payload field name, so they cannot be literals.
 * The caller casts once (see `JobParams.tsx`).
 */
export type JobParamTranslate = (key: string, opts?: { defaultValue?: string }) => string;
type Translate = JobParamTranslate;

/** Sentinel that cannot collide with a real translation. */
const MISSING = '__jobParamLabelMissing__';

const lookup = (t: Translate, key: string): string | null => {
  const value = t(key, { defaultValue: MISSING });
  return value === MISSING ? null : value;
};

/**
 * The label for one entry.
 *
 * Dialog labels are borrowed only for entries under `params`. Elsewhere the
 * same key can mean something entirely different — `deviationAnalysis.title`
 * is the dialog's own heading ("Run deviation analysis"), not a label for
 * `reportOptions.title` — so a nested block's entries are prefixed with their
 * block instead.
 */
export const jobParamLabel = (t: Translate, type: ProcessJobType, entry: JobParamEntry): string => {
  if (entry.inParams) {
    const ns = JOB_TYPE_PARAM_NS[type];
    const fromDialog = ns ? lookup(t, `${ns}.${entry.key}`) : null;
    if (fromDialog) return fromDialog;
  }
  const shared = (key: string) => {
    const alias = SHARED_PARAM_I18N[key];
    return (alias ? lookup(t, alias) : null) ?? humanizeParamKey(key);
  };
  const base = shared(entry.key);
  if (!entry.parentKey || entry.inParams) return base;
  return `${shared(entry.parentKey)} › ${base}`;
};

/** The printed value. Booleans become ticks so "what was checked" reads at a glance. */
export const formatJobParamValue = (entry: JobParamEntry): string => {
  const { key, value } = entry;
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? '✓' : '✗';
  if (typeof value === 'number') {
    return SRID_KEY_RE.test(key) && value > 0 ? `EPSG:${value}` : String(value);
  }
  if (Array.isArray(value)) return value.length === 0 ? '—' : value.map((v) => String(v)).join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};
